import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ReferenceDocument } from '@coin/chat-bot-util-types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'coin-document-reference-card',
  templateUrl: './document-reference-card.component.html',
  styleUrl: './document-reference-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, MatTooltip]
})
export class DocumentReferenceCardComponent {
  private translate = inject(TranslateService);

  document = input.required<ReferenceDocument>();
  download = output<ReferenceDocument>();

  handleDownload() {
    this.download.emit(this.document());
  }

  get noPageInformationText(): string {
    return this.translate.instant('chat-bot.tooltip.no-page-information');
  }

  get singlePageText(): string {
    return this.translate.instant('chat-bot.tooltip.single-page-information');
  }

  get multiplePagesText(): string {
    return this.translate.instant('chat-bot.tooltip.multi-page-information');
  }

  formatPages = computed<string>(() => {
    const document = this.document();
    if (!document || !document.pages || !document.pages.length) return this.noPageInformationText;
    const { pages } = document;

    // Sort & remove duplicates
    const sorted = [...(new Set(pages) as unknown as number[])].sort((a, b) => a - b);

    const ranges: string[] = [];
    let start = sorted[0];
    let prev = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];

      if (current === prev + 1) {
        // still consecutive
        prev = current;
      } else {
        // close the range
        ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
        start = current;
        prev = current;
      }
    }

    // push the last range
    ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
    if (ranges.length > 1) {
      return `${this.multiplePagesText}: ${ranges.join(', ')}`;
    }

    if (ranges.length === 1) {
      return `${this.singlePageText}: ${ranges.join(', ')}`;
    }

    // Fallback if no ranges were found
    return this.noPageInformationText;
  });
}
